/**
 * CART MID PRODUCTOS SYNC
 *
 * Keeps the "MID Productos" Shopify cart attribute in sync with the live cart
 * using the Shopify AJAX Cart API (/cart/update.js).
 *
 * This runs as a lightweight, non-blocking background sync layer.
 * It does NOT modify checkout, cart forms, add-to-cart flows, or any
 * existing cart behavior. The existing hidden input fallback is preserved.
 *
 * Triggers:
 *   - DOMContentLoaded (initial page load)
 *   - ajaxProduct:added  (item added to cart via AJAX)
 *   - cart:updated       (quantity changed, cart rebuilt)
 *
 * Safety:
 *   - Debounced to prevent duplicate requests
 *   - Skips the API call if the value has not changed since last sync
 *   - Fails silently on network errors
 *   - Never blocks checkout or any user interaction
 */

(function () {
  'use strict';

  var ATTRIBUTE_KEY  = 'MID Productos';
  var DEBOUNCE_MS    = 400;

  var _lastSyncedValue = null;
  var _debounceTimer   = null;
  var _syncInFlight    = false;

  /**
   * Fetch the live cart from /cart.js and build a comma-separated string
   * of all product handles currently in the cart.
   *
   * @returns {Promise<string>} e.g. "col1275711, col1275702"
   */
  function getHandlesFromCart() {
    return fetch('/cart.js', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) {
          return '';
        }
        return cart.items
          .map(function (item) { return item.handle; })
          .join(', ');
      });
  }

  /**
   * Persist the "MID Productos" cart attribute via /cart/update.js.
   * Only sends the request if the value has actually changed.
   *
   * @param {string} handles  Comma-separated product handles
   * @returns {Promise<void>}
   */
  function persistAttribute(handles) {
    // Skip if nothing has changed since the last successful sync
    if (handles === _lastSyncedValue) {
      return Promise.resolve();
    }

    _syncInFlight = true;

    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        attributes: {
          [ATTRIBUTE_KEY]: handles
        }
      })
    })
      .then(function () {
        _lastSyncedValue = handles;
      })
      .catch(function () {
        // Fail silently — the hidden input fallback remains active
      })
      .then(function () {
        _syncInFlight = false;
      });
  }

  /**
   * Main sync entry point.
   * Reads the live cart and persists the attribute if the value has changed.
   * Runs asynchronously and never blocks the calling thread.
   */
  function syncMidProductos() {
    getHandlesFromCart()
      .then(function (handles) {
        return persistAttribute(handles);
      })
      .catch(function () {
        // Fail silently
        _syncInFlight = false;
      });
  }

  /**
   * Debounced wrapper around syncMidProductos.
   * Prevents duplicate requests when multiple events fire in quick succession
   * (e.g. quantity stepper rapid clicks, simultaneous cart:updated + ajaxProduct:added).
   */
  function scheduledSync() {
    clearTimeout(_debounceTimer);
    _debounceTimer = setTimeout(function () {
      // If a sync is already in flight, reschedule once more to catch any
      // cart changes that happened while the previous request was pending.
      if (_syncInFlight) {
        _debounceTimer = setTimeout(syncMidProductos, DEBOUNCE_MS);
        return;
      }
      syncMidProductos();
    }, DEBOUNCE_MS);
  }

  // ─── Event listeners ──────────────────────────────────────────────────────

  // Run once on page load to ensure the attribute is current
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncMidProductos);
  } else {
    // DOM already ready (script loaded with defer after parse)
    syncMidProductos();
  }

  // After any item is added to the cart via AJAX
  document.addEventListener('ajaxProduct:added', scheduledSync);

  // After quantity changes or cart drawer rebuilds
  // (fired by theme.CartForm.quantityChanged after changeItem resolves)
  document.addEventListener('cart:updated', scheduledSync);

})();
